import type { TokenVerifyResult } from '@volley/crucible-ipc-types/ipc-channels';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';

/**
 * Build the pre-Create prereq error (issue #308) from a `tokens:verify`
 * response, or return `null` when every probed tool is healthy.
 *
 * Why this is its own module:
 *
 * The CEO hit a misleading "missing system tool: pnpm" error on
 * 2026-05-07 when his pnpm 11.0.8 from Homebrew was installed but
 * crashed at startup with `node:sqlite ERR_UNKNOWN_BUILTIN_MODULE`.
 * Telling him to install pnpm was actively wrong; the fix was
 * `brew uninstall pnpm && npm install -g pnpm@10`. We now
 * distinguish three failure shapes from the IPC layer:
 *
 *   - `'missing-on-path'`  → existing "Missing system tool" copy.
 *   - `'exited-nonzero'`   → "installed but crashed when invoked"
 *     copy that quotes the first lines of stderr verbatim and
 *     suggests the runtime-update remediation.
 *   - `'spawn-failed'`     → "couldn't spawn" copy that punts to
 *     Settings → Diagnostics.
 *
 * Broken-at-runtime takes priority over missing-from-PATH: if any tool
 * is broken, we surface that error first (with its specific
 * remediation) rather than showing a generic missing-tool list.
 *
 * Returns `null` when nothing is wrong — the caller proceeds with
 * Create. Returns a `SerialisedError` with `code` set to
 * `CRUCIBLE-PREREQ-MISSING` (legacy) or `CRUCIBLE-PREREQ-BROKEN`
 * (issue #308) so support docs can reference one code per failure
 * shape.
 */

export type PrereqVerifyShape = Pick<TokenVerifyResult, 'git' | 'node' | 'pnpm'>;

interface ToolEntry {
  key: 'git' | 'node' | 'pnpm';
  label: string;
  status: TokenVerifyResult['git'];
}

function tools(verify: PrereqVerifyShape): ToolEntry[] {
  return [
    { key: 'git', label: 'git', status: verify.git },
    { key: 'node', label: 'Node.js', status: verify.node },
    { key: 'pnpm', label: 'pnpm', status: verify.pnpm },
  ];
}

/** Remediation hint for the `exited-nonzero` bucket. pnpm gets the
 * specific brew-uninstall + npm-install one because it's by far the
 * most common case (CEO report); other tools get a generic copy. */
function brokenRemediationHint(key: ToolEntry['key']): string {
  if (key === 'pnpm') {
    return "This usually means the tool's runtime needs an update (e.g. for pnpm: try `brew uninstall pnpm && npm install -g pnpm@10`).";
  }
  return "This usually means the tool's runtime needs an update — try reinstalling it from the official source.";
}

/**
 * Produce a `SerialisedError` for a single broken-at-runtime tool, or
 * `null` if no tool is in that state.
 */
function buildBrokenError(verify: PrereqVerifyShape): SerialisedError | null {
  const broken = tools(verify).find(
    (t) => !t.status.available && t.status.reason && t.status.reason !== 'missing-on-path',
  );
  if (!broken) return null;
  const { label, status, key } = broken;
  let message: string;
  let recovery: string;
  if (status.reason === 'below-minimum') {
    // Issue #2109: the tool is present and runs, but is older than the
    // version Foundry requires (corepack < 0.31.0 can't download the
    // pinned pnpm). Distinct copy from missing / crashed / spawn-failed
    // so the user updates rather than reinstalls or chases a phantom
    // spawn failure. The pnpm row represents Corepack (issue #310).
    const toolName = key === 'pnpm' ? 'Corepack' : label;
    const detected = status.version ? ` (you have v${status.version})` : '';
    const floor = status.minVersion ? `v${status.minVersion}` : 'the required minimum';
    message = `**${toolName}**${detected} is older than ${floor}, which Foundry requires. Older ${toolName} can't download the pinned pnpm version. Update it with \`npm install -g corepack@latest\`.`;
    recovery =
      'Run `npm install -g corepack@latest && corepack enable`, then open a fresh terminal and try Create again.';
    return {
      name: 'CrucibleError',
      message,
      category: 'state',
      retryable: false,
      code: 'CRUCIBLE-PREREQ-BROKEN',
      recovery,
    };
  }
  if (status.reason === 'exited-nonzero') {
    const stderr = status.stderrFirstLines?.trim();
    const stderrSegment = stderr ? ` The error was: ${stderr}.` : '';
    message = `**${label}** is installed but crashed when invoked.${stderrSegment} ${brokenRemediationHint(key)}`;
    recovery =
      'Re-run Create after fixing the tool. See Settings → Diagnostics for the full failure detail.';
  } else {
    // spawn-failed
    const errnoSegment = status.errno ? ` (errno: ${status.errno})` : '';
    message = `Foundry couldn't spawn **${label}**${errnoSegment}. This is unusual; check Settings → Diagnostics for details.`;
    recovery =
      'Open Settings → Diagnostics to see the full failure detail and platform-specific guidance.';
  }
  return {
    name: 'CrucibleError',
    message,
    category: 'state',
    retryable: false,
    code: 'CRUCIBLE-PREREQ-BROKEN',
    recovery,
  };
}

/**
 * Produce a `SerialisedError` for the missing-from-PATH bucket, or
 * `null` if every tool is on PATH (regardless of whether it works).
 */
function buildMissingError(verify: PrereqVerifyShape): SerialisedError | null {
  const missing: string[] = [];
  // Only flag as "missing" when the IPC layer says so — a
  // broken-at-runtime tool has `available: false` too but should NOT
  // be re-listed as "missing" here.
  const isMissing = (s: TokenVerifyResult['git']): boolean =>
    !s.available && (s.reason === undefined || s.reason === 'missing-on-path');
  if (isMissing(verify.git)) missing.push('git');
  if (isMissing(verify.node)) missing.push('Node.js');
  if (isMissing(verify.pnpm)) missing.push('pnpm');
  if (missing.length === 0) return null;
  return {
    name: 'CrucibleError',
    message: `Missing system tool${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Foundry needs these on PATH to scaffold and run a game.`,
    category: 'state',
    retryable: false,
    code: 'CRUCIBLE-PREREQ-MISSING',
    recovery:
      'Install the missing tool(s) (see Settings → Diagnostics → System tools for platform-specific install commands), then try Create again.',
  };
}

/**
 * Top-level entry. Returns a SerialisedError for the highest-priority
 * problem (broken-at-runtime first, then missing-from-PATH) or `null`
 * when every tool is healthy.
 */
export function buildPrereqError(verify: PrereqVerifyShape): SerialisedError | null {
  return buildBrokenError(verify) ?? buildMissingError(verify);
}
